/**
 * Générateur — Attendu 946 — Construire et mettre en relation différentes
 * représentations en perspectives cavalières des solides suivants : pavé droit,
 * cube, cylindre de révolution, prisme droit.
 * Niveau : 5ème | Domaine : Espace et géométrie / Solides et représentations planes
 *
 * Plan de cartes (version alignée sur la spécification 946 du 2026-09-21) :
 *
 *   Carte « Reconnaître un pavé droit » — QCM d'images (cocher les pavés, le
 *     cube compris) et tableau Vrai/Faux des inclusions entre familles de
 *     solides. Aucun rappel dans la consigne : l'inclusion EST la question.
 *
 *   Carte « Arêtes et faces d'un pavé » — pavé ABCDEFGH en perspective, QCM
 *     « quelle arête est parallèle à … » ou « quelle face est parallèle à … ».
 *     C'est la carte des trois familles d'arêtes (objets_geometriques 3).
 *
 *   Carte « Compter sommets, arêtes, faces » — prisme droit à base à n côtés,
 *     3 ≤ n ≤ 6 ; saisie à trous, toujours « sommets au total » ET « faces
 *     latérales » ensemble.
 *
 *   Carte « La bonne perspective cavalière » — QCM d'images par solide (cube ou
 *     pavé, prisme, cylindre) et tableau Vrai/Faux sur une figure tirée parmi
 *     les quatre solides. Les trois dessins faux matérialisent trois erreurs
 *     visées : fuyantes non parallèles, fuyantes de longueurs inégales, arêtes
 *     cachées en trait plein.
 *
 *   Carte « Le même solide sous un autre angle » — un solide de référence et
 *     quatre perspectives ; cocher celles qui représentent le MÊME solide, vu
 *     sous un autre angle de fuite (second verbe de l'attendu).
 *
 * ── Spécification 946 ──────────────────────────────────────────────────────
 *
 * Bornes appliquées :
 *   solides_au_programme    liste fermée : pavé droit, cube, cylindre de
 *                           révolution, prisme droit (aucune pyramide, aucun cône)
 *   angle_de_fuite          30, 45, 135, 150 — les quatre valeurs MONTANTES seulement,
 *                           le solide est toujours vu de dessus (arbitrage prof 26/09,
 *                           divergence assumée avec la justification de la spec)
 *   coefficient_de_fuite    0,4 .. 0,8  → 0,4 ; 0,5 ; 0,6 ; 0,7 ; 0,8
 *   arete_du_cube           3 .. 7
 *   longueur_de_la_face_avant_du_pave 4 .. 8, toujours > la hauteur
 *   hauteur_de_la_face_avant_du_pave  3 .. 7, ≠ longueur et ≠ profondeur
 *   nombre_de_cotes_de_la_base_du_prisme 3 .. 6
 *   rayon_de_la_base_du_cylindre 1,5 .. 3
 *   hauteur_du_tronc        2 .. 6, toujours > le petit axe de l'ellipse de base
 *   nombre_de_representations_a_mettre_en_relation 2 .. 3
 *   nombre_d_aretes_fournies_dans_l_enonce 1 (une seule fuyante donnée)
 *
 * Exclusions appliquées : aucune pyramide ni cône ni boule, aucun réglage n'y
 * donne accès ; base de prisme jamais au-delà de 6 côtés ; les trois dimensions
 * d'un pavé sont deux à deux distinctes ; l'arête fuyante n'est jamais
 * horizontale ni verticale (les huit angles ont sinus et cosinus non nuls) ;
 * aucune dimension chiffrée sur une figure de perspective ; les arêtes cachées
 * sont TOUJOURS tracées, en pointillés. Leur calcul se fait sur les points
 * EFFECTIVEMENT TRACÉS : le contour du solide est leur enveloppe convexe, et une arête
 * arrière ou une fuyante est cachée si elle tombe strictement à l'intérieur. Ce critère
 * vaut aussi pour les dessins dégradés, dont les reports sont déformés.
 *
 * Erreurs visées → mots-clés :
 *   Ne reporter la fuyante que sur un sommet, perdre le parallélisme
 *                                                    → fuyantes_non_paralleles
 *   Refuser de coder dans une même famille des arêtes d'aspect différent
 *                                                    → famille_aretes_ignoree
 *   Associer systématiquement la profondeur à un raccourcissement
 *                                                    → raccourcissement_suppose
 *   N'avoir mémorisé qu'une seule orientation de la perspective
 *                                                    → orientation_unique
 *   Recopier une arête cachée en trait plein          → cachees_pleines
 *   Ne pas reconnaître un pavé comme prisme, un cube comme pavé
 *                                                    → inclusion_solides_ignoree
 *   Confondre faces latérales et sommets d'une base   → faces_laterales_confondues
 *   Deux angles de fuite différents = deux solides différents
 *                                            → angles_differents_solides_differents
 *
 * Graduation → cartes :
 *   niveau 1 (cube)      perspective_qcm_unique, perspective_tableau_vf
 *   niveau 2 (pavé)      perspective_qcm_unique, relations_qcm_unique, relation_qcm_multiple
 *   niveau 3 (prisme)    perspective_prisme_qcm_unique, perspective_tableau_vf
 *   niveau 4 (cylindre)  perspective_cylindre_qcm_unique, perspective_tableau_vf
 *   niveau 5 (relation)  relation_qcm_multiple
 *   niveau 6 (mélange)   perspective_tableau_vf
 *
 * TOUT PAR CONSTRUCTION : la perspective est une projection oblique exacte, le
 * report est le MÊME vecteur pour tous les sommets, et la visibilité de chaque
 * arête est calculée sur le dessin lui-même, jamais codée en dur. Figures d'énoncé en `imageUrl`, choix de QCM en
 * `imageData` (FORMATS_TANOED §1 et §12).
 */

import {
    randInt, randomChoice, shuffle, makeQcm, makeTexteTrou, makeVraiFaux,
} from '../../../utils';

const ATTENDU_ID = 946;
const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_RECONN = 'Reconnaître un pavé droit';
const L_RELAT = 'Arêtes et faces d’un pavé';
const L_DENOM = 'Compter sommets, arêtes, faces';
const L_PERSP = 'La bonne perspective cavalière';
const L_RELATION = 'Le même solide sous un autre angle';

// ─── Réglages de la perspective (variables de la spec) ───────────────────────

/**
 * Les angles de fuite retenus : tous VERS LE HAUT, c'est-à-dire le solide vu de dessus.
 *
 * La spécification en proposait huit, « vers le haut comme vers le bas », pour montrer
 * qu'une perspective n'a pas qu'une allure canonique. Les quatre angles descendants
 * donnent un solide vu de DESSOUS : le dessin reste juste, mais la face du haut s'y
 * cache et le pointillé se retrouve là où l'élève ne l'attend pas. Le prof a tranché le
 * 2026-09-26 : les solides sont toujours vus de dessus, comme dans les manuels.
 *
 * Les quatre angles conservés fuient toujours à gauche ET à droite : 30 et 45 vers la
 * droite, 135 et 150 vers la gauche. La variété d'orientation que la spécification
 * voulait reste donc servie, sur l'axe horizontal.
 *
 * ⚠️ Divergence assumée avec la justification de la spec, dont la variable est
 * « décidée » et non prescrite : à reporter dans 946.json.
 */
const ANGLES_FUITE = [30, 45, 135, 150];
const COEFS_FUITE = [0.4, 0.5, 0.6, 0.7, 0.8];

const NOIR = '#1f2937';
const R1 = (v) => Number(v).toFixed(2);

/**
 * Enveloppe convexe d'un nuage de points (parcours d'Andrew). Elle donne le CONTOUR du
 * solide dessiné : tout ce qui tombe strictement dedans est masqué par le solide.
 */
const enveloppeConvexe = (pts) => {
    const p = [...pts].sort((a, b) => (a.x - b.x) || (a.y - b.y));
    const croix = (o, a, b) => (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);
    const moitie = (src) => {
        const out = [];
        src.forEach((q) => {
            while (out.length >= 2 && croix(out[out.length - 2], out[out.length - 1], q) <= 0) out.pop();
            out.push(q);
        });
        return out;
    };
    return [...moitie(p).slice(0, -1), ...moitie([...p].reverse()).slice(0, -1)];
};

/** Le point est-il STRICTEMENT à l'intérieur du contour ? (sens de parcours indifférent) */
const strictementDedans = (contour, P, eps = 1e-7) => {
    const signes = contour.map((A, i) => {
        const B = contour[(i + 1) % contour.length];
        return (B.x - A.x) * (P.y - A.y) - (B.y - A.y) * (P.x - A.x);
    });
    return signes.every((s) => s > eps) || signes.every((s) => s < -eps);
};

// ─── Outillage SVG local : perspective cavalière par report ──────────────────

/**
 * Prisme droit couché : `base` est la face avant, donnée en VRAIE GRANDEUR
 * (repère mathématique, y vers le haut, sommets dans le sens direct) ; `prof`
 * est la profondeur. Le report est le même vecteur pour tous les sommets.
 * La visibilité d'une arête arrière se calcule par le signe du produit
 * scalaire de la normale sortante de l'arête avant correspondante et du
 * vecteur de fuite : aucune arête cachée n'est codée en dur, le dessin reste
 * donc juste pour les huit angles de la spec.
 *
 * variante : 'correct' | 'fuyantes_non_paralleles' | 'famille_aretes_ignoree'
 *            | 'cachees_pleines'
 */
const perspectivePrisme = ({
    base, prof, alphaDeg, k, variante = 'correct', noms = null, vb = { w: 168, h: 148 },
}) => {
    const a = (alphaDeg * Math.PI) / 180;
    const n = base.length;
    const dep = k * prof;
    const tMath = { x: dep * Math.cos(a), y: dep * Math.sin(a) };
    // report par sommet : identique partout, sauf dans les variantes dégradées
    const reportDe = (i) => {
        if (variante === 'fuyantes_non_paralleles') {
            const d = (((i % 2 ? 1 : -1) * (9 + 4 * i)) * Math.PI) / 180;
            return { x: dep * Math.cos(a + d), y: dep * Math.sin(a + d) };
        }
        if (variante === 'famille_aretes_ignoree') {
            const f = [1, 0.55, 1.4, 0.75, 1.2, 0.65][i % 6];
            return { x: tMath.x * f, y: tMath.y * f };
        }
        return tMath;
    };
    const avant = base.map((P) => ({ x: P.x, y: P.y }));
    const arriere = base.map((P, i) => { const t = reportDe(i); return { x: P.x + t.x, y: P.y + t.y }; });

    // Visibilité calculée sur les points EFFECTIVEMENT TRACÉS, et non sur le vecteur de
    // fuite nominal. C'est la même chose pour le dessin juste, mais pas pour les dessins
    // dégradés : leurs reports sont déformés, et les pointillés se retrouvaient sur des
    // arêtes qui ne sont pas les cachées du dessin montré. La carte vrai/faux affirme
    // pourtant « la partie cachée est tracée en pointillés » (retour prof 2026-09-26).
    // Mesure avant correction : 1527 arêtes mal marquées sur 18 036 pour les fuyantes
    // non parallèles, 209 sur 18 076 pour les longueurs ignorées, 0 pour le dessin juste.
    //
    // Critère : le contour du solide est l'enveloppe convexe des points tracés ; une
    // arête arrière ou une fuyante est cachée si elle tombe strictement à l'intérieur.
    const contour = enveloppeConvexe([...avant, ...arriere]);
    const milieu = (A, B) => ({ x: (A.x + B.x) / 2, y: (A.y + B.y) / 2 });
    const areteCachee = [];
    for (let i = 0; i < n; i++) {
        areteCachee.push(strictementDedans(contour, milieu(arriere[i], arriere[(i + 1) % n])));
    }
    const sommetCache = arriere.map((P) => strictementDedans(contour, P));

    // passage à l'écran (y vers le bas) + mise à l'échelle uniforme
    const ecran = [...avant, ...arriere].map((P) => ({ x: P.x, y: -P.y }));
    const xs = ecran.map((P) => P.x), ys = ecran.map((P) => P.y);
    const minX = Math.min(...xs), maxX = Math.max(...xs);
    const minY = Math.min(...ys), maxY = Math.max(...ys);
    const pad = 22;
    const s = Math.min((vb.w - 2 * pad) / (maxX - minX || 1), (vb.h - 2 * pad) / (maxY - minY || 1));
    const offX = (vb.w - s * (maxX - minX)) / 2, offY = (vb.h - s * (maxY - minY)) / 2;
    const map = (P) => ({ x: offX + (P.x - minX) * s, y: offY + (P.y - minY) * s });
    const A = avant.map((P) => map({ x: P.x, y: -P.y }));
    const B = arriere.map((P) => map({ x: P.x, y: -P.y }));

    const pointilles = variante !== 'cachees_pleines';
    const out = [`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${vb.w} ${vb.h}" width="${vb.w}" height="${vb.h}" font-family="Arial">`];
    // Les segments tracés sont mémorisés : les noms de sommets s'en écartent (voir plus bas).
    const segments = [];
    const trait = (P, Q, cachee) => {
        segments.push([P, Q]);
        out.push(
            `<line x1="${R1(P.x)}" y1="${R1(P.y)}" x2="${R1(Q.x)}" y2="${R1(Q.y)}" stroke="${NOIR}" stroke-width="1.7" stroke-linecap="round"${cachee && pointilles ? ' stroke-dasharray="5 4"' : ''}/>`,
        );
    };
    for (let i = 0; i < n; i++) trait(B[i], B[(i + 1) % n], areteCachee[i]);   // face arrière
    for (let i = 0; i < n; i++) trait(A[i], B[i], sommetCache[i]);             // arêtes fuyantes
    for (let i = 0; i < n; i++) trait(A[i], A[(i + 1) % n], false);            // face avant
    if (noms) {
        const tous = [...A, ...B];
        const cx = tous.reduce((acc, P) => acc + P.x, 0) / tous.length;
        const cy = tous.reduce((acc, P) => acc + P.y, 0) / tous.length;

        /**
         * Le nom d'un sommet se posait à 12 px du centre de la figure, en ligne droite.
         * Sur un sommet intérieur cette direction longe souvent une arête, et la lettre
         * se retrouvait POSÉE DESSUS (retour prof 2026-09-26, sommets E et C).
         *
         * On essaie maintenant seize directions autour du sommet et on garde celle qui
         * éloigne le plus la lettre de TOUS les traits, en préférant à distance égale
         * celle qui s'écarte du centre de la figure. Les lettres déjà posées comptent
         * comme des obstacles, pour qu'elles ne se chevauchent pas entre elles.
         */
        const distSegment = (P, [S, T]) => {
            const vx = T.x - S.x, vy = T.y - S.y;
            const L2 = vx * vx + vy * vy;
            const u = L2 ? Math.max(0, Math.min(1, ((P.x - S.x) * vx + (P.y - S.y) * vy) / L2)) : 0;
            return Math.hypot(P.x - (S.x + u * vx), P.y - (S.y + u * vy));
        };
        const posees = [];
        tous.forEach((P, i) => {
            const dx = P.x - cx, dy = P.y - cy;
            const dCentre = Math.hypot(dx, dy) || 1;
            let best = null;
            for (let t = 0; t < 16; t++) {
                const th = (t * 2 * Math.PI) / 16;
                const Q = { x: P.x + 13 * Math.cos(th), y: P.y + 13 * Math.sin(th) };
                const auxTraits = Math.min(...segments.map((s) => distSegment(Q, s)));
                const auxNoms = posees.length ? Math.min(...posees.map((R) => Math.hypot(Q.x - R.x, Q.y - R.y))) : 99;
                // sortance : +1 quand la lettre part vers l'extérieur de la figure
                const sortance = (Math.cos(th) * dx + Math.sin(th) * dy) / dCentre;
                const score = Math.min(auxTraits, auxNoms / 2) + sortance;
                if (!best || score > best.score) best = { score, Q };
            }
            posees.push(best.Q);
            out.push(`<text x="${R1(best.Q.x)}" y="${R1(best.Q.y + 4)}" text-anchor="middle" font-size="13" font-weight="700" fill="#1E3A8A">${noms[i]}</text>`);
        });
    }
    out.push('</svg>');
    return out.join('');
};

/**
 * Cylindre de révolution debout : deux ellipses de MÊME dimension, la base
 * éloignée à demi masquée, deux génératrices verticales tangentes. Le demi
 * caché dépend du signe de l'angle de fuite (fuite vers le haut : c'est le
 * demi arrière de la base du bas ; fuite vers le bas : celui de la base du
 * haut), il est donc calculé lui aussi.
 */
const perspectiveCylindre = ({ r, h, alphaDeg, k, variante = 'correct', vb = { w: 168, h: 168 } }) => {
    const a = (alphaDeg * Math.PI) / 180;
    const ry = r * k * Math.abs(Math.sin(a));
    const pad = 20;
    const s = Math.min((vb.w - 2 * pad) / (2 * r), (vb.h - 2 * pad) / (h + 2 * ry));
    const cx = vb.w / 2;
    const rx = r * s, ryS = ry * s;
    const yBas = (vb.h + s * h) / 2, yHaut = (vb.h - s * h) / 2;
    const fuiteVersLeHaut = Math.sin(a) > 0;
    const pointilles = variante !== 'cachees_pleines';
    // génératrices : verticales et parallèles, sauf variante dégradée
    const ecart = variante === 'fuyantes_non_paralleles' ? rx * 0.3 : 0;
    // les deux bases sont superposables, sauf variante dégradée (ouverture de
    // l'ellipse du haut modifiée SEULE : les génératrices restent parallèles)
    const ryHaut = variante === 'famille_aretes_ignoree' ? ryS * 0.38 : ryS;
    const out = [`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${vb.w} ${vb.h}" width="${vb.w}" height="${vb.h}" font-family="Arial">`];
    // demi-ellipse de (cx-rxx ; yc) à (cx+rxx ; yc) : bombée vers le haut
    // (balayage 1) ou vers le bas (balayage 0) en coordonnées écran.
    const demiEllipse = (yc, rxx, ryy, versLeHaut, cachee) => {
        out.push(`<path d="M ${R1(cx - rxx)} ${R1(yc)} A ${R1(rxx)} ${R1(ryy)} 0 0 ${versLeHaut ? 1 : 0} ${R1(cx + rxx)} ${R1(yc)}" fill="none" stroke="${NOIR}" stroke-width="1.7"${cachee && pointilles ? ' stroke-dasharray="5 4"' : ''}/>`);
    };
    const trait = (x1, y1, x2, y2) => out.push(`<line x1="${R1(x1)}" y1="${R1(y1)}" x2="${R1(x2)}" y2="${R1(y2)}" stroke="${NOIR}" stroke-width="1.7" stroke-linecap="round"/>`);
    trait(cx - rx, yBas, cx - rx - ecart, yHaut);
    trait(cx + rx, yBas, cx + rx + ecart, yHaut);
    // Le demi-disque éloigné est celui que le corps du cylindre masque : demi
    // du HAUT de la base du bas quand la fuite monte, demi du BAS de la base du
    // haut quand elle descend.
    demiEllipse(yBas, rx, ryS, false, false);
    demiEllipse(yBas, rx, ryS, true, fuiteVersLeHaut);
    demiEllipse(yHaut, rx + ecart, ryHaut, true, false);
    demiEllipse(yHaut, rx + ecart, ryHaut, false, !fuiteVersLeHaut);
    out.push('</svg>');
    return out.join('');
};

/** Polygone régulier convexe à n côtés (sens direct, un côté horizontal en bas). */
const baseReguliere = (n, cote) => {
    const r = cote / (2 * Math.sin(Math.PI / n));
    const t0 = -Math.PI / 2 + Math.PI / n;
    const pts = [];
    for (let i = 0; i < n; i++) {
        const t = t0 + (2 * Math.PI * i) / n;
        pts.push({ x: r * Math.cos(t), y: r * Math.sin(t) });
    }
    return pts;
};

/** Rectangle L × H, sens direct, coin bas-gauche à l'origine. */
const baseRectangle = (L, H) => [{ x: 0, y: 0 }, { x: L, y: 0 }, { x: L, y: H }, { x: 0, y: H }];

/** Figure d'ÉNONCÉ : imageUrl (jamais imageData, FORMATS_TANOED §12). */
const figEnonce = (svg, maxWidth = 'small', alt = 'Solide en perspective cavalière') => ({
    version: 1,
    elements: [{ type: 'image', config: { imageUrl: `data:image/svg+xml,${encodeURIComponent(svg)}`, maxWidth, alt } }],
});
/** CHOIX de QCM d'images : imageData (FORMATS_TANOED §1). */
const imgChoix = (svg) => ({ type: 'image', config: { imageData: `data:image/svg+xml,${encodeURIComponent(svg)}` } });

// ─── Tirages des solides (bornes de la spec) ─────────────────────────────────

const tirerFuite = () => ({ alphaDeg: randomChoice(ANGLES_FUITE), k: randomChoice(COEFS_FUITE) });

/** Cube : arête 3 à 7. */
const tirerCube = () => { const c = randInt(3, 7); return { solide: 'cube', L: c, H: c, P: c }; };

/** Pavé : longueur 4 à 8 strictement supérieure à la hauteur, trois dimensions distinctes. */
const tirerPave = () => {
    for (let essai = 0; essai < 60; essai++) {
        const L = randInt(4, 8), H = randInt(3, 7), P = randInt(3, 7);
        if (L > H && L !== P && H !== P) return { solide: 'pave', L, H, P };
    }
    return { solide: 'pave', L: 8, H: 5, P: 4 };
};

/** Prisme droit : base régulière à 3 à 6 côtés, longueur 2 à 6. */
const tirerPrisme = () => ({
    solide: 'prisme',
    n: randInt(3, 6),
    cote: randInt(3, 5),
    prof: randInt(2, 6),
});

/** Cylindre : rayon 1,5 à 3 ; hauteur 2 à 6, toujours > le petit axe de la base. */
const tirerCylindre = (fuite) => {
    const r = randomChoice([1.5, 2, 2.5, 3]);
    const petitAxe = 2 * r * fuite.k * Math.abs(Math.sin((fuite.alphaDeg * Math.PI) / 180));
    const hMin = Math.max(2, Math.ceil(petitAxe + 0.5));
    return { solide: 'cylindre', r, h: Math.min(6, Math.max(hMin, randInt(2, 6))) };
};

// Groupes nominaux accordés, élision comprise : jamais un article recollé.
const DE_SOLIDE = {
    cube: "d'un cube", pave: "d'un pavé droit",
    prisme: "d'un prisme droit", cylindre: "d'un cylindre de révolution",
};

/** Dessin d'un solide tiré, dans une variante donnée. */
const dessiner = (sol, fuite, variante, vb, noms = null) => {
    if (sol.solide === 'cylindre') {
        return perspectiveCylindre({ r: sol.r, h: sol.h, ...fuite, variante, vb: vb || { w: 168, h: 168 } });
    }
    const base = sol.solide === 'prisme' ? baseReguliere(sol.n, sol.cote) : baseRectangle(sol.L, sol.H);
    const prof = sol.solide === 'prisme' ? sol.prof : sol.P;
    return perspectivePrisme({ base, prof, ...fuite, variante, noms, vb: vb || { w: 168, h: 148 } });
};

// Trois dessins faux, chacun sur une erreur visée nommée.
const VARIANTES_FAUSSES = [
    { code: 'fuyantes_non_paralleles', variante: 'fuyantes_non_paralleles' },
    { code: 'famille_aretes_ignoree', variante: 'famille_aretes_ignoree' },
    { code: 'cachees_pleines', variante: 'cachees_pleines' },
];

/** La fuyante paraît-elle plus longue que le plus court côté de la face avant ? */
const fuyanteLongue = (sol, fuite) => {
    if (sol.solide === 'cylindre') return false;
    const court = sol.solide === 'prisme' ? sol.cote : Math.min(sol.L, sol.H);
    const prof = sol.solide === 'prisme' ? sol.prof : sol.P;
    return fuite.k * prof > court;
};

const erreursDeFigure = (sol, fuite) => {
    const codes = ['orientation_unique'];
    if (fuyanteLongue(sol, fuite)) codes.push('raccourcissement_suppose');
    return codes;
};

// ═══════════════════════════════════════════════════════════
// Carte 1 — Reconnaître un pavé droit
// ═══════════════════════════════════════════════════════════

const genReconnaitre = () => {
    const vb = { w: 150, h: 132 };
    const FAUX_SOLIDES = [() => tirerPrisme(), (f) => tirerCylindre(f), () => tirerPrisme(), (f) => tirerCylindre(f)];
    let planche = null;
    for (let essai = 0; essai < 40 && !planche; essai++) {
        const fait = [];
        const img = (make) => {
            const f = tirerFuite();
            const sol = make(f);
            const svg = dessiner(sol, f, 'correct', vb);
            fait.push({ sol, f, svg });
            return svg;
        };
        const nbCorrects = randInt(1, 2);                   // un pavé, ou un pavé et un cube
        const bons = [img(() => tirerPave())];
        if (nbCorrects === 2) bons.push(img(() => tirerCube()));
        const faux = shuffle(FAUX_SOLIDES).slice(0, 4 - nbCorrects).map((make) => img(make));
        // quatre dessins deux à deux distincts : sinon makeQcm en fusionne deux
        // et la planche n'aurait plus quatre propositions
        if (new Set([...bons, ...faux]).size === 4) planche = { fait, bons, faux };
    }
    if (!planche) {                                          // repli déterministe
        const sols = [{ solide: 'pave', L: 8, H: 5, P: 3 }, { solide: 'prisme', n: 3, cote: 4, prof: 5 },
            { solide: 'prisme', n: 5, cote: 3, prof: 6 }, { solide: 'cylindre', r: 2, h: 5 }];
        const svgs = sols.map((s, i) => dessiner(s, { alphaDeg: ANGLES_FUITE[i], k: 0.5 }, 'correct', vb));
        planche = {
            fait: sols.map((s, i) => ({ sol: s, f: { alphaDeg: ANGLES_FUITE[i], k: 0.5 }, svg: svgs[i] })),
            bons: [svgs[0]], faux: [svgs[1], svgs[2], svgs[3]],
        };
    }
    const carte = new Map();
    [...planche.bons, ...planche.faux].forEach((svg) => carte.set(svg, imgChoix(svg)));
    const corrects = planche.bons.map((svg) => carte.get(svg));
    const choix = shuffle([...planche.bons, ...planche.faux].map((svg) => carte.get(svg)));
    const texte = '<p>Voici des solides dessinés en perspective cavalière. '
        + '<b>Coche celui (ou ceux) qui est (sont) un pavé droit.</b></p>';
    const payload = makeQcm(choix, corrects, texte, opts(L_RECONN), true);
    payload._meta.params = {
        niveau: 'n2',
        solides: planche.fait.map((x) => x.sol.solide),
        angles: planche.fait.map((x) => x.f.alphaDeg),
        erreursVisees: ['inclusion_solides_ignoree', 'orientation_unique'],
    };
    return payload;
};

// Inclusions entre familles de solides : deux énoncés vrais, deux faux, mélangés.
const INCLUSIONS_VRAIES = [
    'Un cube est un pavé droit.',
    'Un pavé droit est un prisme droit.',
    'Un cube est un prisme droit.',
    'Les deux bases d’un prisme droit sont superposables.',
];
const INCLUSIONS_FAUSSES = [
    'Tout prisme droit est un pavé droit.',
    'Tout pavé droit est un cube.',
    'Un cylindre de révolution est un prisme droit.',
    'Un prisme droit à base triangulaire est un pavé droit.',
];

const genReconnaitreVf = () => {
    const vraies = shuffle(INCLUSIONS_VRAIES).slice(0, 2);
    const fausses = shuffle(INCLUSIONS_FAUSSES).slice(0, 2);
    const lignes = shuffle([...vraies.map((l) => [l, 0]), ...fausses.map((l) => [l, 1])]);
    const payload = makeVraiFaux(lignes.map((x) => x[0]), lignes.map((x) => x[1]),
        '<p><b>Vrai ou faux ?</b></p>', opts(L_RECONN));
    payload._meta.params = { niveau: 'n2', erreursVisees: ['inclusion_solides_ignoree'] };
    return payload;
};

// ═══════════════════════════════════════════════════════════
// Carte 2 — Arêtes // et faces d'un pavé nommé ABCDEFGH
// ═══════════════════════════════════════════════════════════

// Face avant ABFE (A bas-gauche, B bas-droite, F haut-droite, E haut-gauche),
// face arrière DCGH obtenue par le report : la base est ABCD, le dessus EFGH.
const NOMS_PAVE = ['A', 'B', 'F', 'E', 'D', 'C', 'G', 'H'];
const tri2 = (s) => s.split('').sort().join('');
const CLASSES_ARETES = [
    ['AB', 'CD', 'EF', 'GH'].map(tri2),   // longueur
    ['BC', 'AD', 'FG', 'EH'].map(tri2),   // profondeur (les fuyantes)
    ['AE', 'BF', 'CG', 'DH'].map(tri2),   // hauteur
];
// Faces nommées dans l'ordre cyclique de leurs sommets (jamais tri alphabétique :
// « ABEF » ne décrit aucun contour).
const PAIRES_FACES = [
    ['ABCD', 'EFGH'],
    ['ABFE', 'DCGH'],
    ['ADHE', 'BCGF'],
];

const genRelations = () => {
    const sol = tirerPave();
    const fuite = tirerFuite();
    const svg = dessiner(sol, fuite, 'correct', { w: 250, h: 210 }, NOMS_PAVE);
    const estArete = randomChoice([true, true, false]);   // 2/3 arêtes, 1/3 faces
    let texte, choix, correct, distracteurs;
    if (estArete) {
        const classe = randomChoice(CLASSES_ARETES);
        const ref = randomChoice(classe);
        const bon = randomChoice(classe.filter((x) => x !== ref));
        const autres = shuffle(CLASSES_ARETES.filter((c) => c !== classe).flat()).slice(0, 3);
        distracteurs = autres.map((x) => ({ code: 'famille_aretes_ignoree', valeur: `[${x}]` }));
        choix = shuffle([bon, ...autres]).map((x) => `[${x}]`);
        correct = `[${bon}]`;
        texte = '<p>ABCDEFGH est un pavé droit (voir la figure). '
            + `<b>Quelle arête est parallèle à l'arête [${ref}] ?</b></p>`;
    } else {
        const paire = randomChoice(PAIRES_FACES);
        const ref = randomChoice(paire);
        const bon = paire.find((f) => f !== ref);
        const autres = shuffle(PAIRES_FACES.filter((p) => p !== paire).flat()).slice(0, 3);
        distracteurs = autres.map((x) => ({ code: 'famille_aretes_ignoree', valeur: x }));
        choix = shuffle([bon, ...autres]);
        correct = bon;
        texte = '<p>ABCDEFGH est un pavé droit (voir la figure). '
            + `<b>Quelle face est parallèle à la face ${ref} ?</b></p>`;
    }
    const payload = makeQcm(choix, correct, texte, {
        ...opts(L_RELAT), elements_additionnels: figEnonce(svg, 'medium', 'Pavé droit ABCDEFGH en perspective cavalière'),
    });
    payload._meta.params = {
        niveau: 'n2', solide: 'pave', dims: [sol.L, sol.H, sol.P],
        angle: fuite.alphaDeg, coef: fuite.k,
        distracteurs, erreursVisees: erreursDeFigure(sol, fuite),
    };
    return payload;
};

// ═══════════════════════════════════════════════════════════
// Carte 3 — Dénombrer sommets / arêtes / faces d'un prisme
// ═══════════════════════════════════════════════════════════

const BASES = [
    { n: 3, nom: 'triangle' },
    { n: 4, nom: 'quadrilatère' },
    { n: 5, nom: 'pentagone' },
    { n: 6, nom: 'hexagone' },
];

// Les deux grandeurs toujours demandées ensemble portent l'erreur visée 7.
const GRANDEURS_CLES = [
    { val: (n) => 2 * n, label: 'sommets au total' },
    { val: (n) => n, label: 'faces latérales' },
];
const GRANDEURS_AUTRES = [
    { val: (n) => 3 * n, label: 'arêtes au total' },
    { val: (n) => n + 2, label: 'faces au total' },
];

const genDenombrer = () => {
    const { n, nom } = randomChoice(BASES);
    const autres = shuffle(GRANDEURS_AUTRES).slice(0, randInt(0, 2));
    const choisies = shuffle([...GRANDEURS_CLES, ...autres]);
    // Un dénombrement par ligne : l'impression coupait la ligne au milieu quand
    // deux trous indépendants partageaient une même ligne.
    const phrase = choisies.map((g) => `{{${g.val(n)}}} ${g.label}`).join('\n');
    const texte = `<p>On considère un prisme droit dont la base est un <b>${nom}</b> `
        + `(polygone à ${n} côtés). Complète en comptant :</p>`;
    const payload = makeTexteTrou(phrase, texte, { ...opts(L_DENOM), mode: 'saisie', strict: false });
    payload._meta.params = { niveau: 'n3', n, erreursVisees: ['faces_laterales_confondues'] };
    return payload;
};

// ═══════════════════════════════════════════════════════════
// Carte 4 — La bonne perspective cavalière
// ═══════════════════════════════════════════════════════════

/** Mesures du solide, pour la traçabilité du harnais et du test de spécification. */
const mesuresDe = (sol) => (sol.solide === 'cylindre'
    ? { r: sol.r, h: sol.h }
    : (sol.solide === 'prisme'
        ? { n: sol.n, cote: sol.cote, prof: sol.prof }
        : { dims: [sol.L, sol.H, sol.P] }));

const NIVEAU_DU_SOLIDE = { cube: 'n1', pave: 'n2', prisme: 'n3', cylindre: 'n4' };

const genPerspectiveQcm = (tireur) => () => {
    const fuite = tirerFuite();
    const sol = tireur(fuite);
    const vb = sol.solide === 'cylindre' ? { w: 150, h: 156 } : { w: 150, h: 132 };
    const bon = imgChoix(dessiner(sol, fuite, 'correct', vb));
    const faux = VARIANTES_FAUSSES.map((v) => imgChoix(dessiner(sol, fuite, v.variante, vb)));
    const choix = shuffle([bon, ...faux]);
    const texte = '<p>Un seul de ces dessins est une <b>perspective cavalière correcte</b> '
        + `${DE_SOLIDE[sol.solide]}. <b>Lequel ?</b></p>`
        + '<p><i>Rappel : sur une perspective cavalière, les arêtes fuyantes sont parallèles '
        + 'entre elles et de même longueur, et les arêtes cachées se tracent en pointillés.</i></p>';
    const payload = makeQcm(choix, bon, texte, opts(L_PERSP));
    payload._meta.params = {
        niveau: NIVEAU_DU_SOLIDE[sol.solide], solide: sol.solide, ...mesuresDe(sol),
        angle: fuite.alphaDeg, coef: fuite.k,
        distracteurs: VARIANTES_FAUSSES.map((v) => ({ code: v.code, valeur: v.variante })),
        erreursVisees: erreursDeFigure(sol, fuite),
    };
    return payload;
};

const tirerCubeOuPave = () => randomChoice([tirerCube, tirerPave])();
const tirerSolideQuelconque = (fuite) => randomChoice([
    () => tirerCube(), () => tirerPave(), () => tirerPrisme(), () => tirerCylindre(fuite),
])();

/** Vrai/Faux LU SUR LA FIGURE : les verdicts dépendent de la variante tirée. */
const genPerspectiveVf = () => {
    const fuite = tirerFuite();
    const sol = tirerSolideQuelconque(fuite);
    const choixVariante = randomChoice(['correct', ...VARIANTES_FAUSSES.map((v) => v.variante)]);
    const vb = sol.solide === 'cylindre' ? { w: 190, h: 198 } : { w: 200, h: 176 };
    const svg = dessiner(sol, fuite, choixVariante, vb);
    const estCyl = sol.solide === 'cylindre';
    const lignes = [
        {
            texte: estCyl
                ? 'Sur ce dessin, les deux génératrices du cylindre sont parallèles.'
                : 'Sur ce dessin, les arêtes fuyantes sont parallèles entre elles.',
            vrai: choixVariante !== 'fuyantes_non_paralleles',
        },
        {
            texte: estCyl
                ? 'Sur ce dessin, les deux bases du cylindre ont la même dimension.'
                : 'Sur ce dessin, les arêtes fuyantes ont toutes la même longueur.',
            vrai: choixVariante !== 'famille_aretes_ignoree',
        },
        {
            texte: 'Sur ce dessin, la partie cachée est tracée en pointillés.',
            vrai: choixVariante !== 'cachees_pleines',
        },
        {
            texte: 'Ce dessin est une perspective cavalière correcte.',
            vrai: choixVariante === 'correct',
        },
    ];
    const melange = shuffle(lignes);
    const payload = makeVraiFaux(melange.map((l) => l.texte), melange.map((l) => (l.vrai ? 0 : 1)),
        `<p>Voici le dessin ${DE_SOLIDE[sol.solide]}. <b>Vrai ou faux ?</b></p>`,
        { ...opts(L_PERSP), elements_additionnels: figEnonce(svg, 'medium') });
    payload._meta.params = {
        niveau: 'n6', solide: sol.solide, variante: choixVariante, ...mesuresDe(sol),
        angle: fuite.alphaDeg, coef: fuite.k,
        erreursVisees: [
            ...erreursDeFigure(sol, fuite),
            ...(choixVariante === 'correct' ? [] : [choixVariante]),
        ],
    };
    return payload;
};

// ═══════════════════════════════════════════════════════════
// Carte 5 — Le même solide sous un autre angle (mise en relation)
// ═══════════════════════════════════════════════════════════

/** Deux pavés sont le même solide si le triplet trié de leurs dimensions coïncide. */
const memeTriplet = (a, b) => {
    const t = (s) => [s.L, s.H, s.P].sort((x, y) => x - y).join('|');
    return t(a) === t(b);
};

/**
 * Les proportions d'un pavé : ses deux plus grandes dimensions rapportées à la plus
 * petite. C'est à elles que l'œil reconnaît un solide, la taille du dessin étant fixée
 * par le cadre.
 */
const proportionsDe = (s) => {
    const t = [s.L, s.H, s.P].sort((x, y) => x - y);
    return [t[1] / t[0], t[2] / t[0]];
};

/**
 * Le solide « différent » doit se distinguer À L'ŒIL, car les dessins ne portent aucune
 * cote et sont vus sous des angles de fuite différents.
 *
 * Un triplet simplement distinct ne suffisait pas : 3×4×5 contre 3×4×6 sortait une fois
 * sur dix, et 3×4×5 contre 5×7×8 a des proportions à 0,07 l'une de l'autre. L'élève ne
 * pouvait pas trancher, et se voyait refuser des propositions qui lui semblaient
 * identiques (retour prof 2026-09-26).
 */
const ECART_PROPORTIONS_MIN = 0.5;
const assezDifferent = (a, b) => {
    const [pa, pb] = [proportionsDe(a), proportionsDe(b)];
    return Math.max(Math.abs(pa[0] - pb[0]), Math.abs(pa[1] - pb[1])) >= ECART_PROPORTIONS_MIN;
};

const genRelation = () => {
    const ref = randomChoice([tirerCube, tirerPave])();
    let autre = null;
    for (let essai = 0; essai < 200 && !autre; essai++) {
        const cand = tirerPave();
        if (!memeTriplet(cand, ref) && assezDifferent(cand, ref)) autre = cand;
    }
    // Repli : deux formes franchement opposées, la plus allongée et la plus trapue du
    // domaine. L'une des deux est toujours loin de la référence.
    if (!autre) {
        autre = [{ solide: 'pave', L: 8, H: 3, P: 4 }, { solide: 'pave', L: 5, H: 4, P: 3 }]
            .find(c => !memeTriplet(c, ref) && assezDifferent(c, ref)) || null;
    }
    // Dernier filet : une forme très allongée, loin de tout pavé du domaine.
    if (!autre) autre = { solide: 'pave', L: 8, H: 3, P: 4 };
    // Angles de fuite tous différents ; coefficient COMMUN à la planche, pour que
    // seule la profondeur distingue les solides (et non l'échelle du dessin).
    const k = randomChoice(COEFS_FUITE);
    // Quatre angles suffisent : un pour la référence, trois pour les propositions.
    const angles = shuffle(ANGLES_FUITE).slice(0, 4);
    const vb = { w: 150, h: 132 };
    const svgRef = dessiner(ref, { alphaDeg: angles[0], k }, 'correct', { w: 190, h: 168 });
    const nbMemes = randInt(1, 2);      // 2 ou 3 représentations du même solide, référence comprise
    const memes = [];
    for (let i = 0; i < nbMemes; i++) memes.push(imgChoix(dessiner(ref, { alphaDeg: angles[i + 1], k }, 'correct', vb)));
    const differents = [];
    for (let i = nbMemes; i < 3; i++) differents.push(imgChoix(dessiner(autre, { alphaDeg: angles[i + 1], k }, 'correct', vb)));
    const choix = shuffle([...memes, ...differents]);
    const texte = `<p>Le solide de référence est dessiné ci-dessus, en perspective cavalière. `
        + '<b>Coche le (ou les) dessin(s) qui représente(nt) le MÊME solide, vu sous un autre '
        + 'angle de fuite.</b></p>';
    const payload = makeQcm(choix, memes, texte, {
        ...opts(L_RELATION), elements_additionnels: figEnonce(svgRef, 'medium', 'Solide de référence en perspective cavalière'),
    }, true);
    payload._meta.params = {
        niveau: 'n5', solide: ref.solide,
        dims: [ref.L, ref.H, ref.P], dimsAutre: [autre.L, autre.H, autre.P],
        angles, coef: k, nbRepresentations: nbMemes + 1,
        erreursVisees: ['angles_differents_solides_differents', 'orientation_unique'],
    };
    return payload;
};

// ═══════════════════════════════════════════════════════════
// REGISTRE
// ═══════════════════════════════════════════════════════════

const GENERATORS = {
    reconnaitre_qcm_multiple: genReconnaitre,
    reconnaitre_tableau_vf: genReconnaitreVf,
    relations_qcm_unique: genRelations,
    denombrer_texte_trou_saisie: genDenombrer,
    perspective_qcm_unique: genPerspectiveQcm(tirerCubeOuPave),
    perspective_prisme_qcm_unique: genPerspectiveQcm(() => tirerPrisme()),
    perspective_cylindre_qcm_unique: genPerspectiveQcm((f) => tirerCylindre(f)),
    perspective_tableau_vf: genPerspectiveVf,
    relation_qcm_multiple: genRelation,
};

const FORMAT_LABELS = {
    qcm_multiple: 'QCM images', qcm_unique: 'QCM',
    tableau_vf: 'Vrai/Faux', texte_trou_saisie: 'Saisie',
    prisme_qcm_unique: 'QCM prisme', cylindre_qcm_unique: 'QCM cylindre',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter((k) => k.startsWith(`${prefix}_`))
        .map((k) => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'reconnaitre', label: L_RECONN, formatsList: buildFormats('reconnaitre') },
    { id: 'relations', label: L_RELAT, formatsList: buildFormats('relations') },
    { id: 'denombrer', label: L_DENOM, formatsList: buildFormats('denombrer') },
    { id: 'perspective', label: L_PERSP, formatsList: buildFormats('perspective') },
    { id: 'relation', label: L_RELATION, formatsList: buildFormats('relation') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
